import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  DrawerProps,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { Close, ExpandLess, ExpandMore, MenuOpen, Search } from '@material-ui/icons';
import BIServiceCRMPrimary from 'assets/icons/svg/BIServiceCRMPrimary';
import { USER_OPERATIONS } from 'helper';
import { RootState } from 'interfaces';

import { SIDEBAR_MENU } from './menu';
import { SideBarNavMenuItem } from './types';

interface Props extends DrawerProps {}

const SIDEBAR_WIDTH = 360;

const isExternalPath = (path: string) => /^https?:\/\//.test(path);

const createItemKey = (item: { path?: string; name?: string; groupName?: string }, index: number) => {
  return item.path ?? item.name ?? item.groupName ?? String(index);
};

const isSubheaderOnly = (item: {
  groupName?: string;
  path?: string;
  children?: SideBarNavMenuItem['children'];
  name?: string;
}) => {
  return Boolean(item.groupName) && !item.path && !(item.children && item.children.length);
};

// Пропсы ссылки пункта: внешний путь уходит в <a target="_blank">, внутренний — в react-router Link.
// Тип any — как в исходном коде: подмена component у ListItem иначе не типизируется.
const buildLinkProps = (path: string | undefined, onClick: () => void): any => {
  if (!path) {
    return {};
  }

  if (isExternalPath(path)) {
    return { component: 'a', href: path, target: '_blank', rel: 'noopener noreferrer', onClick };
  }

  return { component: Link, to: path, onClick };
};

const SideBarSubheader: React.FC<{ title?: string; nested?: boolean }> = ({ title, nested }) => (
  <>
    <ListItem style={{ padding: 0 }} divider />
    <ListSubheader
      component="div"
      disableSticky
      style={{
        paddingLeft: nested ? 32 : undefined,
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        lineHeight: '38px',
      }}
    >
      <b>{title}</b>
    </ListSubheader>
    <ListItem style={{ padding: 0 }} divider />
  </>
);

const SideBarItem: React.FC<{
  item: Omit<SideBarNavMenuItem, 'children'>;
  selected: boolean;
  nested?: boolean;
  onClick: () => void;
}> = ({ item, selected, nested, onClick }) => {
  const IconComponent = item.Icon;

  return (
    <ListItem
      button
      selected={selected}
      style={nested ? { paddingLeft: 32 } : undefined}
      {...buildLinkProps(item.path, onClick)}
    >
      {IconComponent && (
        <ListItemIcon style={nested ? { minWidth: 32 } : undefined}>
          <IconComponent />
        </ListItemIcon>
      )}
      <ListItemText primary={item.name} primaryTypographyProps={{ noWrap: true }} />
    </ListItem>
  );
};

// Группа со сворачиванием и поиском по своим пунктам вынесена из SideBar: пока вся разметка
// жила внутри filteredMenuItems.map, cognitive complexity колбэка была 30 при пороге 15 (S3776)
const SideBarGroup: React.FC<{
  item: SideBarNavMenuItem;
  isOpen: boolean;
  isSelected: boolean;
  query: string;
  onToggle: () => void;
  onQueryChange: (query: string) => void;
  isPathActive: (path?: string) => boolean;
  onItemClick: () => void;
}> = ({ item, isOpen, isSelected, query, onToggle, onQueryChange, isPathActive, onItemClick }) => {
  const IconComponent = item.Icon;
  const children = item.children ?? [];
  const normalizedQuery = item.searchable ? query.trim().toLowerCase() : '';
  // При активном поиске подзаголовки групп (groupName без name) отсеиваются сами —
  // иначе в результатах остались бы висящие заголовки без пунктов.
  const visibleChildren = normalizedQuery
    ? children.filter((child) => (child.name || '').toLowerCase().includes(normalizedQuery))
    : children;

  return (
    <>
      <ListItem button selected={isSelected} onClick={onToggle}>
        {IconComponent && (
          <ListItemIcon>
            <IconComponent />
          </ListItemIcon>
        )}
        <ListItemText primary={item.name} primaryTypographyProps={{ noWrap: true }} />
        {isOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItem>

      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {item.searchable && (
            <ListItem style={{ paddingLeft: 32, paddingRight: 16 }}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Поиск по справочникам"
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" htmlColor="#7b8698" />
                    </InputAdornment>
                  ),
                  endAdornment: query ? (
                    <InputAdornment position="end">
                      <IconButton size="small" title="Очистить" onClick={() => onQueryChange('')}>
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </ListItem>
          )}

          {Boolean(normalizedQuery) && !visibleChildren.length && (
            <ListItem style={{ paddingLeft: 32 }}>
              <ListItemText secondary="Ничего не найдено" />
            </ListItem>
          )}

          {visibleChildren.map((child, childIndex) => {
            const childKey = createItemKey(child, childIndex);

            if (isSubheaderOnly(child)) {
              return <SideBarSubheader key={`${childKey}-subheader`} title={child.groupName} nested />;
            }

            return (
              <SideBarItem
                key={childKey}
                item={child}
                nested
                selected={isPathActive(child.path)}
                onClick={onItemClick}
              />
            );
          })}
        </List>
      </Collapse>
    </>
  );
};

const SideBar = ({ onClose, PaperProps, open, ...restDrawerProps }: Props) => {
  const operations = useSelector((state: RootState) => state.operations);
  const location = useLocation();

  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  // Поиск внутри группы (item.searchable) — в «Технических справочниках» больше сорока пунктов.
  // Ключ тот же, что у openItems, поэтому запрос живёт отдельно у каждой группы.
  const [groupQueries, setGroupQueries] = useState<Record<string, string>>({});

  const paperProps = useMemo(
    () => ({
      ...(PaperProps ?? {}),
      style: { ...(PaperProps?.style ?? {}), width: SIDEBAR_WIDTH },
    }),
    [PaperProps],
  );

  const closeDrawer = useCallback(() => {
    onClose?.({}, 'backdropClick');
  }, [onClose]);

  const hasRequiredOperations = useCallback(
    (requiredOperations: SideBarNavMenuItem['operations']) => {
      if (!requiredOperations?.length) {
        return true;
      }

      return requiredOperations.some((operation) => {
        const operationName = USER_OPERATIONS[operation] || operation;
        return operations.includes(operationName);
      });
    },
    [operations],
  );

  const filteredMenuItems = useMemo(() => {
    return SIDEBAR_MENU.reduce<SideBarNavMenuItem[]>((acc, item) => {
      if (!hasRequiredOperations(item.operations)) {
        return acc;
      }

      const { children, ...rest } = item;
      const accessibleChildren = children?.filter((child) => hasRequiredOperations(child.operations));

      if (children && !accessibleChildren?.length && !item.path && !item.groupName) {
        return acc;
      }

      const filteredItem: SideBarNavMenuItem = {
        ...rest,
        ...(accessibleChildren?.length ? { children: accessibleChildren } : {}),
      };

      acc.push(filteredItem);
      return acc;
    }, []);
  }, [hasRequiredOperations]);

  const isPathActive = useCallback(
    (path?: string) => {
      if (!path || isExternalPath(path)) {
        return false;
      }

      return location.pathname === path;
    },
    [location.pathname],
  );

  useEffect(() => {
    setOpenItems((prev) => {
      let updated = false;
      const nextState = { ...prev };

      filteredMenuItems.forEach((item, index) => {
        if (!item.children?.length) {
          return;
        }

        const key = createItemKey(item, index);
        const shouldBeOpen = item.children.some((child) => isPathActive(child.path));

        if (shouldBeOpen && !prev[key]) {
          nextState[key] = true;
          updated = true;
        }
      });

      return updated ? nextState : prev;
    });
  }, [filteredMenuItems, isPathActive]);

  const toggleItem = useCallback((key: string) => {
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    // Группа открывается и закрывается с чистым поиском — иначе свёрнутый фильтр прячет пункты
    setGroupQueries((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));
  }, []);

  const setGroupQuery = useCallback((key: string, query: string) => {
    setGroupQueries((prev) => ({ ...prev, [key]: query }));
  }, []);

  const handleItemClick = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  // Меню закрыли — поиск по группам сбрасывается, иначе при следующем открытии список
  // остаётся отфильтрованным прошлым запросом
  useEffect(() => {
    if (!open) {
      setGroupQueries({});
    }
  }, [open]);

  return (
    <Drawer {...restDrawerProps} open={open} onClose={onClose} PaperProps={paperProps}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          height: 52,
        }}
      >
        <BIServiceCRMPrimary />

        <Tooltip title="Закрыть меню">
          <IconButton onClick={closeDrawer}>
            <MenuOpen />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider />

      <List component="nav" disablePadding style={{ overflow: 'auto', flexGrow: 1, scrollbarWidth: 'thin' }}>
        {filteredMenuItems.map((item, index) => {
          const itemKey = createItemKey(item, index);

          if (isSubheaderOnly(item)) {
            return <SideBarSubheader key={`${itemKey}-subheader`} title={item.groupName} />;
          }

          if (item.children?.length) {
            const hasActiveChild = item.children.some((child) => isPathActive(child.path));

            return (
              <SideBarGroup
                key={itemKey}
                item={item}
                isOpen={Boolean(openItems[itemKey])}
                isSelected={isPathActive(item.path) || hasActiveChild}
                query={groupQueries[itemKey] || ''}
                onToggle={() => toggleItem(itemKey)}
                onQueryChange={(query) => setGroupQuery(itemKey, query)}
                isPathActive={isPathActive}
                onItemClick={handleItemClick}
              />
            );
          }

          return <SideBarItem key={itemKey} item={item} selected={isPathActive(item.path)} onClick={handleItemClick} />;
        })}
      </List>
    </Drawer>
  );
};

export default SideBar;
